import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Zap, PiggyBank, Check, Info, ExternalLink, Calculator as CalcIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    Tooltip as RTooltip,
    Legend,
} from "recharts";
import FieldTooltip from "../components/FieldTooltip";
import Disclaimer from "../components/Disclaimer";
import Feedback from "../components/Feedback";
import ContactCard from "../components/ContactCard";
import {
    api,
    formatApiError,
    trackEvent,
    STATES,
    CHILDCARE_TYPES,
    CHILD_ORDERS,
    formatAUD,
    formatAUDc,
} from "../lib/ccsApi";

const CONTRIB_COLORS = { subsidy: "#22C55E", parent: "#1E3A8A" };
const STEP = { DETAILS: 0, RESULTS: 1 };

const ACTIVITY_HELP_URL = "https://www.servicesaustralia.gov.au/recognised-participation-and-activity-test-for-child-care-subsidy";

function emptySibling() { return { age: "", childcare_type: "", hours_per_week: "" }; }

export default function Calculator() {
    const [search, setSearch] = useSearchParams();
    const initialMode = search.get("mode") === "out_of_pocket" ? "out_of_pocket" : "estimator";
    const [mode, setMode] = useState(initialMode);
    const [step, setStep] = useState(STEP.DETAILS);

    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [resultMode, setResultMode] = useState(null);

    const [estForm, setEstForm] = useState({
        state: "",
        childcare_type: "",
        family_income: "",
        activity_hours_per_fortnight: "",
        target_age: "",
        target_order: "first",
        sibling1: emptySibling(),
        sibling2: emptySibling(),
    });

    const [oopForm, setOopForm] = useState({
        childcare_type: "",
        ccs_percent: "",
        fee_type: "hourly",
        hourly_fee: "",
        daily_fee: "",
        charged_hours_per_day: "",
        days_per_fortnight: "",
        subsidised_hours_per_fortnight: "",
    });

    useEffect(() => { trackEvent("page_view", { page: "calculator", mode }); }, [mode]);

    // Prefill from query (used by the CTA after CCS estimate)
    useEffect(() => {
        if (search.get("mode") === "out_of_pocket") {
            const ccs = search.get("ccs");
            const subs = search.get("subs");
            if (ccs || subs) {
                setOopForm((f) => ({
                    ...f,
                    ccs_percent: ccs ?? f.ccs_percent,
                    subsidised_hours_per_fortnight: subs ?? f.subsidised_hours_per_fortnight,
                }));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function switchMode(m, extra = {}) {
        setMode(m);
        setSearch({ mode: m, ...extra });
        setResult(null);
        setStep(STEP.DETAILS);
    }

    // ----- Validation -----
    function validateEstimator() {
        const f = estForm;
        if (!f.state) return "Please select your state or territory.";
        if (!f.childcare_type) return "Please select a childcare type.";
        if (f.family_income === "" || Number(f.family_income) < 0) return "Enter your annual family income.";
        if (f.activity_hours_per_fortnight === "" || Number(f.activity_hours_per_fortnight) < 0) return "Enter your activity hours per fortnight.";
        if (f.target_age === "" || Number(f.target_age) < 0 || Number(f.target_age) > 18) return "Enter the target child's age (0–18).";
        if (f.target_order === "second") return validateSibling(f.sibling1, "first child");
        if (f.target_order === "third_or_more") {
            return validateSibling(f.sibling1, "first child") || validateSibling(f.sibling2, "second child");
        }
        return null;
    }
    function validateSibling(s, label) {
        if (s.age === "" || Number(s.age) < 0 || Number(s.age) > 18) return `Please enter a valid age for the ${label}.`;
        if (!s.childcare_type) return `Please select a childcare type for the ${label}.`;
        if (s.hours_per_week === "" || Number(s.hours_per_week) < 0 || Number(s.hours_per_week) > 100) return `Please enter the ${label}'s hours in care per week (0–100).`;
        return null;
    }
    function validateOOP() {
        const f = oopForm;
        if (!f.childcare_type) return "Please select a childcare type.";
        if (f.ccs_percent === "" || Number(f.ccs_percent) < 0 || Number(f.ccs_percent) > 100) return "CCS % must be between 0 and 100.";
        if (f.fee_type === "hourly") {
            if (f.hourly_fee === "" || Number(f.hourly_fee) <= 0) return "Enter the centre's hourly fee.";
        } else {
            if (f.daily_fee === "" || Number(f.daily_fee) <= 0) return "Enter the centre's daily/session fee.";
        }
        if (f.charged_hours_per_day === "" || Number(f.charged_hours_per_day) <= 0) return "Enter hours charged per day.";
        if (f.days_per_fortnight === "" || Number(f.days_per_fortnight) < 1 || Number(f.days_per_fortnight) > 14) return "Days per fortnight must be 1–14.";
        if (f.subsidised_hours_per_fortnight === "" || Number(f.subsidised_hours_per_fortnight) < 0) return "Enter the approved subsidised hours per fortnight.";
        return null;
    }

    // ----- Submit -----
    async function submit() {
        const err = mode === "estimator" ? validateEstimator() : validateOOP();
        if (err) { toast.error(err); return; }
        setSubmitting(true);
        try {
            let data;
            if (mode === "estimator") {
                const siblings = [];
                if (estForm.target_order === "second" || estForm.target_order === "third_or_more") {
                    siblings.push({
                        age: Number(estForm.sibling1.age),
                        childcare_type: estForm.sibling1.childcare_type,
                        hours_per_week: Number(estForm.sibling1.hours_per_week),
                    });
                }
                if (estForm.target_order === "third_or_more") {
                    siblings.push({
                        age: Number(estForm.sibling2.age),
                        childcare_type: estForm.sibling2.childcare_type,
                        hours_per_week: Number(estForm.sibling2.hours_per_week),
                    });
                }
                const res = await api.post("/ccs/estimate", {
                    state: estForm.state,
                    childcare_type: estForm.childcare_type,
                    family_income: Number(estForm.family_income),
                    activity_hours_per_fortnight: Number(estForm.activity_hours_per_fortnight),
                    target_child: { age: Number(estForm.target_age), order: estForm.target_order },
                    siblings,
                });
                data = res.data;
            } else {
                const body = {
                    childcare_type: oopForm.childcare_type,
                    ccs_percent: Number(oopForm.ccs_percent),
                    fee_type: oopForm.fee_type,
                    charged_hours_per_day: Number(oopForm.charged_hours_per_day),
                    days_per_fortnight: Number(oopForm.days_per_fortnight),
                    subsidised_hours_per_fortnight: Number(oopForm.subsidised_hours_per_fortnight),
                };
                if (oopForm.fee_type === "hourly") body.hourly_fee = Number(oopForm.hourly_fee);
                else body.daily_fee = Number(oopForm.daily_fee);
                const res = await api.post("/ccs/out-of-pocket", body);
                data = res.data;
            }
            setResult(data);
            setResultMode(mode);
            setStep(STEP.RESULTS);
            window.scrollTo({ top: 0, behavior: "smooth" });
            trackEvent("calculation_completed", { mode });
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSubmitting(false);
        }
    }

    function goBack() {
        setResult(null);
        setStep(STEP.DETAILS);
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    // CCS estimate result has CTA → switch to out-of-pocket prefilled
    function gotoOOPFromEstimate() {
        if (!result) return;
        const ccs = result.result.target_child.ccs_percent;
        const subs = result.result.subsidised_hours_per_fortnight;
        switchMode("out_of_pocket", { ccs: String(ccs), subs: String(subs) });
        setOopForm((f) => ({
            ...f,
            ccs_percent: String(ccs),
            subsidised_hours_per_fortnight: String(subs),
        }));
    }

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
            <ModeSwitcher mode={mode} onSwitch={switchMode} />

            {step === STEP.DETAILS && mode === "estimator" && (
                <EstimatorForm form={estForm} setForm={setEstForm} onSubmit={submit} submitting={submitting} />
            )}
            {step === STEP.DETAILS && mode === "out_of_pocket" && (
                <OOPForm form={oopForm} setForm={setOopForm} onSubmit={submit} submitting={submitting} />
            )}

            {step === STEP.RESULTS && result && (
                <Results
                    mode={resultMode}
                    data={result}
                    onBack={goBack}
                    onGotoOOP={gotoOOPFromEstimate}
                />
            )}

            <div className="mt-10">
                <Disclaimer />
            </div>
        </div>
    );
}

function ModeSwitcher({ mode, onSwitch }) {
    return (
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="mode-switcher">
            <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Calculator</span>
            <div className="inline-flex p-1 bg-[#F1F5F9] border border-[#E2E8F0] rounded-full self-start">
                <button
                    type="button"
                    onClick={() => onSwitch("estimator")}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${mode === "estimator" ? "bg-white text-[#1E3A8A] shadow-sm" : "text-slate-600 hover:text-[#1E3A8A]"}`}
                    data-testid="mode-toggle-estimator"
                >
                    <Zap className="h-3.5 w-3.5 mr-1 inline" /> Estimate CCS percentage
                </button>
                <button
                    type="button"
                    onClick={() => onSwitch("out_of_pocket")}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${mode === "out_of_pocket" ? "bg-white text-[#1E3A8A] shadow-sm" : "text-slate-600 hover:text-[#1E3A8A]"}`}
                    data-testid="mode-toggle-oop"
                >
                    <PiggyBank className="h-3.5 w-3.5 mr-1 inline" /> Calculate out-of-pocket cost
                </button>
            </div>
        </div>
    );
}

// ----- Estimator form -----
function EstimatorForm({ form, setForm, onSubmit, submitting }) {
    function setField(k, v) { setForm((f) => ({ ...f, [k]: v })); }
    function setSibling(key, k, v) { setForm((f) => ({ ...f, [key]: { ...f[key], [k]: v } })); }

    return (
        <div className="ccs-card animate-fade-up" data-testid="estimator-form">
            <h2 className="font-heading font-semibold text-2xl text-[#0F172A]">Estimate your CCS percentage</h2>
            <p className="mt-1 text-slate-600 text-sm">Tell us about your situation and we'll work out the rate that should apply.</p>

            <div className="mt-6 grid sm:grid-cols-2 gap-5">
                <Field label="State or territory" id="state" tip="Where you live in Australia.">
                    <Select value={form.state} onValueChange={(v) => setField("state", v)}>
                        <SelectTrigger className="mt-1.5 min-h-[44px] bg-white" data-testid="input-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                        <SelectContent>{STATES.map((s) => (<SelectItem key={s.code} value={s.code} data-testid={`option-state-${s.code}`}>{s.label}</SelectItem>))}</SelectContent>
                    </Select>
                </Field>

                <Field label="Childcare type" id="cct" tip="The type of service the target child attends.">
                    <Select value={form.childcare_type} onValueChange={(v) => setField("childcare_type", v)}>
                        <SelectTrigger className="mt-1.5 min-h-[44px] bg-white" data-testid="input-childcare-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                        <SelectContent>{CHILDCARE_TYPES.map((t) => (<SelectItem key={t.value} value={t.value} data-testid={`option-cct-${t.value}`}>{t.label}</SelectItem>))}</SelectContent>
                    </Select>
                </Field>

                <Field label="Annual family income (AUD)" id="income" tip="Combined household income before tax. Example: $120,000 per year.">
                    <Input type="number" min="0" placeholder="e.g. 120000" value={form.family_income}
                        onChange={(e) => setField("family_income", e.target.value)} className="mt-1.5 min-h-[44px]" data-testid="input-family-income" />
                </Field>

                <Field
                    label="Activity hours per fortnight"
                    id="activity"
                    tip={
                        <>
                            Activity hours are recognised work, study, training or other approved activities used by Services Australia to determine subsidised hours.{" "}
                            <a href={ACTIVITY_HELP_URL} target="_blank" rel="noreferrer" className="underline text-blue-200" data-testid="activity-help-link">Learn more</a>.
                        </>
                    }
                >
                    <Input type="number" min="0" placeholder="e.g. 38" value={form.activity_hours_per_fortnight}
                        onChange={(e) => setField("activity_hours_per_fortnight", e.target.value)} className="mt-1.5 min-h-[44px]" data-testid="input-activity-hours" />
                </Field>
            </div>

            {/* Target child */}
            <div className="mt-8 bg-white border border-[#E2E8F0] rounded-xl p-5">
                <h3 className="font-heading font-semibold text-lg text-[#0F172A]">Which child are you calculating the CCS for?</h3>
                <p className="text-sm text-slate-500 mt-1">Pick the child this calculation is about.</p>

                <div className="mt-4 grid sm:grid-cols-2 gap-5">
                    <Field label="Child position" id="target-order" tip="Is this your first, second or third+ child? Used to apply the correct CCS rate.">
                        <Select value={form.target_order} onValueChange={(v) => setField("target_order", v)}>
                            <SelectTrigger className="mt-1.5 min-h-[44px] bg-white" data-testid="input-target-order"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {CHILD_ORDERS.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`option-order-${o.value}`}>{o.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Age of this child" id="target-age" tip="Children aged 5 or younger may qualify for higher subsidies in multi-child families.">
                        <Input type="number" min="0" max="18" placeholder="e.g. 3" value={form.target_age}
                            onChange={(e) => setField("target_age", e.target.value)} className="mt-1.5 min-h-[44px]" data-testid="input-target-age" />
                    </Field>
                </div>
            </div>

            {(form.target_order === "second" || form.target_order === "third_or_more") && (
                <SiblingBlock
                    title="First child details"
                    note="The CCS uplift depends on whether you have an older child aged 5 or under in approved childcare."
                    sibling={form.sibling1}
                    setSibling={(k, v) => setSibling("sibling1", k, v)}
                    testidPrefix="sibling-1"
                />
            )}
            {form.target_order === "third_or_more" && (
                <SiblingBlock
                    title="Second child details"
                    note="Required to determine whether the higher CCS rate applies to this child."
                    sibling={form.sibling2}
                    setSibling={(k, v) => setSibling("sibling2", k, v)}
                    testidPrefix="sibling-2"
                />
            )}

            <div className="mt-6 bg-[#F0FDF4] border border-[#22C55E]/30 rounded-xl p-4 text-sm text-slate-700 flex items-start gap-3" data-testid="three-day-guarantee-note">
                <Info className="h-4 w-4 text-[#16A34A] mt-0.5 flex-shrink-0" />
                <div>
                    <strong className="text-[#0F172A]">3 Day Guarantee:</strong>{" "}
                    From 5 January 2026, the Australian Government's 3 Day Guarantee provides eligible families with at least 72 hours of subsidised child care per fortnight (about 3 days per week) regardless of activity levels.{" "}
                    <span className="text-slate-500">This affects subsidised hours only — your CCS percentage is still determined by your family income.</span>
                </div>
            </div>

            <div className="mt-8 flex justify-end">
                <Button type="button" onClick={onSubmit} disabled={submitting} className="ccs-btn-primary rounded-full" data-testid="submit-estimator">
                    {submitting ? "Calculating…" : "Calculate"} <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
            </div>
        </div>
    );
}

function SiblingBlock({ title, note, sibling, setSibling, testidPrefix }) {
    return (
        <div className="mt-6 bg-white border-l-4 border-[#1E3A8A] border-t border-r border-b border-[#E2E8F0] rounded-xl p-5" data-testid={`${testidPrefix}-block`}>
            <h3 className="font-heading font-semibold text-lg text-[#0F172A]">{title}</h3>
            <p className="text-sm text-slate-500 mt-1">{note}</p>
            <div className="mt-4 grid sm:grid-cols-3 gap-5">
                <Field label="Age" id={`${testidPrefix}-age`} tip="The child's current age in years.">
                    <Input type="number" min="0" max="18" placeholder="e.g. 4" value={sibling.age}
                        onChange={(e) => setSibling("age", e.target.value)} className="mt-1.5 min-h-[44px]" data-testid={`input-${testidPrefix}-age`} />
                </Field>
                <Field label="Childcare type" id={`${testidPrefix}-cct`} tip="The type of approved childcare this child attends.">
                    <Select value={sibling.childcare_type} onValueChange={(v) => setSibling("childcare_type", v)}>
                        <SelectTrigger className="mt-1.5 min-h-[44px] bg-white" data-testid={`input-${testidPrefix}-cct`}><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                            {CHILDCARE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </Field>
                <Field label="Hours in care per week" id={`${testidPrefix}-hours`} tip="Total hours per week the child is in approved care. Enter 0 if not currently in care.">
                    <Input type="number" min="0" max="100" placeholder="e.g. 30" value={sibling.hours_per_week}
                        onChange={(e) => setSibling("hours_per_week", e.target.value)} className="mt-1.5 min-h-[44px]" data-testid={`input-${testidPrefix}-hours`} />
                </Field>
            </div>
        </div>
    );
}

function Field({ label, id, tip, children }) {
    return (
        <div>
            <Label className="flex items-center text-sm font-medium text-[#0F172A]">
                {label}
                {tip && <FieldTooltip id={id} text={tip} />}
            </Label>
            {children}
        </div>
    );
}

// ----- Out-of-pocket form -----
function OOPForm({ form, setForm, onSubmit, submitting }) {
    function setField(k, v) { setForm((f) => ({ ...f, [k]: v })); }

    return (
        <div className="ccs-card animate-fade-up" data-testid="oop-form">
            <h2 className="font-heading font-semibold text-2xl text-[#0F172A]">Calculate out-of-pocket cost</h2>
            <p className="mt-1 text-slate-600 text-sm">You already know your CCS %. We'll show your fortnightly gap fee and a monthly estimate.</p>

            <div className="mt-6 grid sm:grid-cols-2 gap-5">
                <Field label="Childcare type" id="oop-cct" tip="The type of service. Determines the FY 2025-26 hourly rate cap used in the subsidy formula.">
                    <Select value={form.childcare_type} onValueChange={(v) => setField("childcare_type", v)}>
                        <SelectTrigger className="mt-1.5 min-h-[44px] bg-white" data-testid="input-oop-childcare-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                        <SelectContent>{CHILDCARE_TYPES.map((t) => (<SelectItem key={t.value} value={t.value} data-testid={`oop-option-cct-${t.value}`}>{t.label}</SelectItem>))}</SelectContent>
                    </Select>
                </Field>

                <Field label="CCS percentage" id="oop-pct" tip="The CCS % the government has approved for your family. Example: 85% means the subsidy covers 85% of the eligible hourly rate.">
                    <Input type="number" min="0" max="100" placeholder="e.g. 85" value={form.ccs_percent}
                        onChange={(e) => setField("ccs_percent", e.target.value)} className="mt-1.5 min-h-[44px]" data-testid="input-ccs-percent" />
                </Field>
            </div>

            <div className="mt-6">
                <Label className="flex items-center text-sm font-medium text-[#0F172A]">
                    How does your childcare centre charge?
                    <FieldTooltip id="fee-type" text="Hourly fee (typical for Family Day Care) or a flat daily/session fee (typical for Long Day Care)." />
                </Label>
                <div className="mt-2 inline-flex p-1 bg-white border border-[#E2E8F0] rounded-full">
                    <button
                        type="button"
                        onClick={() => setField("fee_type", "hourly")}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${form.fee_type === "hourly" ? "bg-[#1E3A8A] text-white" : "text-slate-600 hover:text-[#1E3A8A]"}`}
                        data-testid="fee-type-hourly"
                    >Hourly fee</button>
                    <button
                        type="button"
                        onClick={() => setField("fee_type", "daily")}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${form.fee_type === "daily" ? "bg-[#1E3A8A] text-white" : "text-slate-600 hover:text-[#1E3A8A]"}`}
                        data-testid="fee-type-daily"
                    >Daily fee</button>
                </div>
            </div>

            <div className="mt-4 grid sm:grid-cols-2 gap-5">
                {form.fee_type === "hourly" ? (
                    <Field label="Hourly fee charged (AUD)" id="hourly-fee" tip="The hourly rate your centre charges. If higher than the government cap, you pay the difference out-of-pocket.">
                        <Input type="number" min="0" step="0.01" placeholder="e.g. 13.50" value={form.hourly_fee}
                            onChange={(e) => setField("hourly_fee", e.target.value)} className="mt-1.5 min-h-[44px]" data-testid="input-hourly-fee" />
                    </Field>
                ) : (
                    <Field label="Daily / session fee (AUD)" id="daily-fee" tip="The flat daily fee your centre charges. Example: $162 per day.">
                        <Input type="number" min="0" step="0.01" placeholder="e.g. 162" value={form.daily_fee}
                            onChange={(e) => setField("daily_fee", e.target.value)} className="mt-1.5 min-h-[44px]" data-testid="input-daily-fee" />
                    </Field>
                )}
                <Field
                    label="Hours charged per day"
                    id="charged-hours-per-day"
                    tip="The number of hours the centre charges for each day (not how long your child is physically there). Often 10–12 hours for Long Day Care."
                >
                    <Input type="number" min="0" max="24" step="0.25" placeholder="e.g. 10" value={form.charged_hours_per_day}
                        onChange={(e) => setField("charged_hours_per_day", e.target.value)} className="mt-1.5 min-h-[44px]" data-testid="input-charged-hours-per-day" />
                </Field>
            </div>

            <div className="mt-4 grid sm:grid-cols-2 gap-5">
                <Field label="Days per fortnight" id="days-per-fortnight" tip="How many days in a 2-week period your child attends. 10 = 5 days each week.">
                    <Input type="number" min="1" max="14" placeholder="e.g. 10" value={form.days_per_fortnight}
                        onChange={(e) => setField("days_per_fortnight", e.target.value)} className="mt-1.5 min-h-[44px]" data-testid="input-days-per-fortnight" />
                </Field>
                <Field
                    label="Subsidised hours per fortnight (approved)"
                    id="subs-hours"
                    tip="The hours of subsidy approved by the government per fortnight. Common values: 72 (3 Day Guarantee / 16-48 activity hrs) or 100 (48+ activity hrs)."
                >
                    <Input type="number" min="0" max="200" step="1" placeholder="e.g. 100" value={form.subsidised_hours_per_fortnight}
                        onChange={(e) => setField("subsidised_hours_per_fortnight", e.target.value)} className="mt-1.5 min-h-[44px]" data-testid="input-subsidised-hours" />
                </Field>
            </div>

            <div className="mt-8 flex justify-end">
                <Button type="button" onClick={onSubmit} disabled={submitting} className="ccs-btn-primary rounded-full" data-testid="submit-oop">
                    {submitting ? "Calculating…" : "Calculate"} <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
            </div>
        </div>
    );
}

// ----- Results -----
function Results({ mode, data, onBack, onGotoOOP }) {
    const r = data.result;
    return (
        <div className="space-y-6 animate-fade-up">
            <div className="flex items-center justify-between gap-3">
                <Button type="button" variant="outline" onClick={onBack} className="rounded-full" data-testid="back-button-top">
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back
                </Button>
                <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Latest CCS rules</span>
            </div>

            <div>
                <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Your results</span>
                <h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl text-[#0F172A] mt-2" data-testid="results-title">
                    {mode === "estimator" ? "Your estimated CCS" : "Your out-of-pocket costs"}
                </h2>
            </div>

            {mode === "estimator" ? (
                <>
                    <EstimatorResults r={r} />
                    <CalculationNotes />
                    <NextStepCTA onGotoOOP={onGotoOOP} />
                </>
            ) : (
                <OOPResults r={r} />
            )}

            <Feedback mode={mode} calculationId={data.calculation_id} />

            <EmailOptIn mode={mode} data={data} />

            <ContactCard />

            <div className="flex flex-col sm:flex-row gap-3">
                <Button type="button" variant="outline" onClick={onBack} className="rounded-full" data-testid="back-button-bottom">
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back
                </Button>
            </div>
        </div>
    );
}

function NextStepCTA({ onGotoOOP }) {
    return (
        <div className="ccs-card border-[#1E3A8A]/20 bg-[#F8FAFC]" data-testid="oop-cta-card">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="h-11 w-11 rounded-full bg-[#22C55E] text-white flex items-center justify-center flex-shrink-0">
                    <CalcIcon className="h-5 w-5" />
                </div>
                <div className="flex-1">
                    <h3 className="font-heading font-semibold text-lg text-[#0F172A]">Want to estimate your out-of-pocket cost?</h3>
                    <p className="text-sm text-slate-600 mt-1">Give me your childcare fee and charged hours, and I'll help estimate what you may pay.</p>
                </div>
                <Button type="button" onClick={onGotoOOP} className="ccs-btn-secondary rounded-full whitespace-nowrap" data-testid="cta-goto-oop">
                    Calculate my out-of-pocket cost <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
            </div>
        </div>
    );
}

function EstimatorResults({ r }) {
    const t = r.target_child;
    return (
        <div className="grid gap-4">
            <div className="grid md:grid-cols-3 gap-4">
                <StatCard label="CCS for this child" value={`${t.ccs_percent}%`} hint={t.rate_type === "higher" ? "Higher rate applied" : "Standard rate"} testid="stat-target-ccs" accent />
                <StatCard label="Standard CCS %" value={`${r.standard_ccs_percent}%`} testid="stat-standard-ccs" />
                <StatCard label="Subsidised hours / fortnight" value={`${r.subsidised_hours_per_fortnight} hrs`} hint={r.three_day_guarantee_applied ? "3 Day Guarantee may apply" : ""} testid="stat-subsidised-hours" />
            </div>

            <div className="ccs-card" data-testid="uplift-explanation">
                <h3 className="font-heading font-semibold text-lg text-[#0F172A]">Why this rate?</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{r.uplift_reason}</p>
                {r.higher_ccs_eligible && (
                    <p className="mt-2 text-sm text-[#16A34A] font-medium">Higher CCS available: {r.higher_ccs_percent}%</p>
                )}
                {t.ccs_percent === 0 && (
                    <p className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="zero-ccs-note">
                        Your estimated CCS is <strong>0%</strong>, so your subsidy amount will be $0 even if you receive subsidised hours under the 3 Day Guarantee.
                    </p>
                )}
            </div>

            {r.siblings && r.siblings.length > 0 && (
                <div className="ccs-card" data-testid="sibling-summary">
                    <h3 className="font-heading font-semibold text-lg text-[#0F172A]">Older children in care</h3>
                    <ul className="mt-3 space-y-2 text-sm">
                        {r.siblings.map((s, i) => (
                            <li key={i} className="flex items-center justify-between bg-white rounded-lg border border-[#E2E8F0] px-3 py-2">
                                <span className="text-[#0F172A]">{i === 0 ? "First" : "Second"} child — age {s.age}</span>
                                <span className="text-slate-500">{s.hours_per_week} hrs / week · {labelForType(s.childcare_type)}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function OOPResults({ r }) {
    const b = r.breakdown;
    const chart = useMemo(() => ([
        { name: "Government subsidy", value: r.totals.fortnight_subsidy, fill: CONTRIB_COLORS.subsidy },
        { name: "You pay (gap)", value: r.totals.fortnight_parent, fill: CONTRIB_COLORS.parent },
    ]), [r]);

    return (
        <div className="grid gap-4">
            {/* Headline numbers */}
            <div className="grid md:grid-cols-3 gap-4">
                <StatCard label="Gap per day" value={formatAUDc(r.totals.daily_parent)} testid="stat-daily-parent" />
                <StatCard label="Gap per fortnight" value={formatAUDc(r.totals.fortnight_parent)} testid="stat-fortnight-parent" accent />
                <StatCard label="Approx monthly estimate" value={formatAUDc(r.totals.monthly_parent)} testid="stat-monthly-parent" />
            </div>

            {/* Cap-exceeded callout */}
            {r.fee_above_cap && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3" data-testid="cap-exceeded-callout">
                    <Info className="h-5 w-5 text-amber-700 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-amber-900 leading-relaxed">
                        <strong>Your hourly fee is above the CCS hourly cap.</strong>{" "}
                        Your childcare centre charges {formatAUDc(b.D_cap.your_hourly_fee)}/hour, but the CCS hourly cap is {formatAUDc(b.D_cap.gov_cap)}/hour for {labelForType(r.childcare_type)}. Your subsidy is calculated only on the capped amount — the {formatAUDc(r.fee_above_cap_amount)}/hour difference is paid in full by you.
                    </div>
                </div>
            )}

            {/* Invoice-style breakdown */}
            <div className="ccs-card" data-testid="oop-breakdown">
                <h3 className="font-heading font-semibold text-xl text-[#0F172A]">How we calculated this</h3>
                <p className="text-sm text-slate-500 mt-1">Step-by-step — like an invoice.</p>

                <div className="mt-5 divide-y divide-[#E2E8F0] bg-white rounded-xl border border-[#E2E8F0]">
                    <InvoiceRow n="1" label="Full childcare fee" value={formatAUDc(b.A_full_fee.value)} note={b.A_full_fee.formula} />
                    <InvoiceRow n="2" label="Charged hours per fortnight" value={`${b.B_charged_hours.value} hrs`} note={b.B_charged_hours.formula} />
                    <InvoiceRow
                        n="3"
                        label="Approved subsidised hours"
                        value={`${b.C_subsidised_hours.used} / ${b.C_subsidised_hours.approved} hrs used`}
                        note={
                            b.C_subsidised_hours.unsubsidised > 0
                                ? `${b.C_subsidised_hours.unsubsidised} unsubsidised hrs charged at full parent cost.`
                                : `All charged hours are within your approved subsidised hours.`
                        }
                    />
                    <InvoiceRow
                        n="4"
                        label="CCS hourly cap applied"
                        value={formatAUDc(b.D_cap.gov_cap)}
                        note={b.D_cap.note}
                        flag={b.D_cap.cap_used ? "cap" : null}
                    />
                    <InvoiceRow n="5" label="Gross CCS subsidy" value={formatAUDc(b.E_subsidy.value)} note={b.E_subsidy.formula} />
                    <InvoiceRow n="6" label={`${r.withholding_percent}% government withholding`} value={`− ${formatAUDc(b.F_withholding.value)}`} note={b.F_withholding.formula} negative />
                    <InvoiceRow n="7" label="Net CCS paid to provider" value={formatAUDc(b.G_net_ccs.value)} note={b.G_net_ccs.formula} positive />
                    <InvoiceRow n="8" label="Estimated parent gap (fortnight)" value={formatAUDc(b.H_gap.fortnight_value)} note={b.H_gap.fortnight_formula} bold />
                    <InvoiceRow n="9" label="Approx monthly estimate" value={formatAUDc(b.H_gap.monthly_value)} note={b.H_gap.monthly_formula} bold />
                </div>

                <div className="mt-3 text-sm text-slate-600">
                    Your gap per day: <strong className="text-[#0F172A]">{formatAUDc(b.H_gap.daily_value)}</strong>{" "}
                    <span className="text-slate-400">({b.H_gap.daily_formula})</span>
                </div>
            </div>

            <CalculationNotes />

            <div className="grid md:grid-cols-2 gap-4">
                <div className="ccs-card" data-testid="contribution-chart">
                    <h3 className="font-heading font-semibold text-lg text-[#0F172A]">Fortnight contribution split</h3>
                    <div className="h-64 mt-2 min-w-0">
                        <ResponsiveContainer width="99%" height="100%" minWidth={0}>
                            <PieChart>
                                <Pie data={chart} dataKey="value" nameKey="name" innerRadius={60} outerRadius={90} paddingAngle={2}>
                                    {chart.map((c, i) => <Cell key={i} fill={c.fill} />)}
                                </Pie>
                                <RTooltip formatter={(v) => formatAUD(v)} />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="ccs-card" data-testid="totals-breakdown">
                    <h3 className="font-heading font-semibold text-lg text-[#0F172A]">Fortnight breakdown</h3>
                    <dl className="mt-3 space-y-2 text-sm">
                        <Row k="Full fortnight fee" v={formatAUD(r.totals.fortnight_gross)} />
                        <Row k={`Net CCS paid (after ${r.withholding_percent}% withholding)`} v={`- ${formatAUD(r.totals.fortnight_subsidy)}`} className="text-[#16A34A]" />
                        <Row k="Your fortnight gap" v={formatAUDc(r.totals.fortnight_parent)} bold />
                        <div className="my-2 h-px bg-[#E2E8F0]" />
                        <Row k="Yearly: you pay" v={formatAUD(r.totals.yearly_parent)} />
                        <Row k="Yearly: subsidy" v={formatAUD(r.totals.yearly_subsidy)} />
                    </dl>
                </div>
            </div>
        </div>
    );
}

function InvoiceRow({ n, label, value, note, bold, negative, positive, flag }) {
    return (
        <div className="grid grid-cols-[2rem_1fr_auto] gap-3 items-start p-4">
            <div className="h-7 w-7 rounded-full bg-[#1E3A8A] text-white font-heading font-bold text-xs flex items-center justify-center">{n}</div>
            <div>
                <div className={`text-sm ${bold ? "font-heading font-bold text-[#0F172A]" : "text-[#0F172A]"}`}>{label}</div>
                {note && <div className="text-xs text-slate-500 mt-0.5 font-mono break-words">{note}</div>}
                {flag === "cap" && <div className="text-xs text-amber-700 mt-1">Cap applied (lower than your fee)</div>}
            </div>
            <div className={`text-right font-heading font-semibold whitespace-nowrap ${bold ? "text-lg text-[#0F172A]" : "text-base text-[#0F172A]"} ${negative ? "text-amber-700" : ""} ${positive ? "text-[#16A34A]" : ""}`}>
                {value}
            </div>
        </div>
    );
}

function CalculationNotes() {
    return (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-500 leading-relaxed" data-testid="calculation-notes">
            <strong className="text-slate-700">Note:</strong> Actual childcare invoices may differ due to absences, provider adjustments, backdated CCS changes, Centrelink balancing, additional fees, or account updates.
        </div>
    );
}

function Step({ letter, title, children, accent }) {
    return (
        <div className={`flex gap-4 p-4 rounded-xl border ${accent ? "bg-[#22C55E]/10 border-[#22C55E]/30" : "bg-white border-[#E2E8F0]"}`}>
            <div className={`h-9 w-9 rounded-full flex items-center justify-center font-heading font-bold text-sm flex-shrink-0 ${accent ? "bg-[#22C55E] text-white" : "bg-[#1E3A8A] text-white"}`}>
                {letter}
            </div>
            <div className="text-sm text-slate-700 flex-1">
                <div className="font-heading font-semibold text-[#0F172A]">{title}</div>
                <div className="mt-2">{children}</div>
            </div>
        </div>
    );
}

function Mono({ children, className = "" }) {
    return <div className={`font-mono text-sm bg-white border border-[#E2E8F0] rounded-lg p-3 break-all ${className}`}>{children}</div>;
}

function KV({ k, v }) {
    return (
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-2.5">
            <div className="text-xs text-slate-500 uppercase tracking-[0.15em]">{k}</div>
            <div className="font-heading font-semibold text-[#0F172A] mt-0.5">{v}</div>
        </div>
    );
}

function Row({ k, v, bold, className = "" }) {
    return (
        <div className="flex items-center justify-between">
            <dt className="text-slate-600">{k}</dt>
            <dd className={`${bold ? "font-heading font-bold text-[#0F172A] text-base" : ""} ${className}`}>{v}</dd>
        </div>
    );
}

function StatCard({ label, value, hint, testid, accent }) {
    return (
        <div className={`rounded-xl border p-5 ${accent ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white border-[#E2E8F0]"}`} data-testid={testid}>
            <div className={`text-xs uppercase tracking-[0.2em] ${accent ? "text-blue-100" : "text-slate-500"}`}>{label}</div>
            <div className={`mt-2 font-heading font-bold text-3xl ${accent ? "text-white" : "text-[#0F172A]"}`}>{value}</div>
            {hint && <div className={`text-xs mt-1 ${accent ? "text-blue-100" : "text-slate-500"}`}>{hint}</div>}
        </div>
    );
}

function labelForType(t) {
    return CHILDCARE_TYPES.find((x) => x.value === t)?.label || t || "—";
}

// ---------- Email opt-in ----------
function EmailOptIn({ mode, data }) {
    const [email, setEmail] = useState("");
    const [consent, setConsent] = useState(false);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    async function submit(e) {
        e.preventDefault();
        if (!email) { toast.error("Please enter your email"); return; }
        setSending(true);
        try {
            const html = buildSummaryHtml(mode, data);
            const res = await api.post("/email/send-results", {
                recipient_email: email,
                mode,
                summary_html: html,
                calculation_id: data.calculation_id,
                consent_marketing: consent,
            });
            if (res.data.status === "success") toast.success("Email sent. Check your inbox.");
            else toast.success("Request received.");
            setSent(true);
            trackEvent("email_optin", { mode });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSending(false);
        }
    }

    if (sent) {
        return (
            <div className="ccs-card border-[#22C55E]/40 bg-[#F0FDF4]" data-testid="email-optin-success">
                <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-[#22C55E] text-white flex items-center justify-center"><Check className="h-5 w-5" /></div>
                    <div>
                        <div className="font-heading font-semibold text-[#0F172A]">Thanks — we've sent your results.</div>
                        <div className="text-sm text-slate-600">We'll email you a copy to review later.</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="ccs-card" data-testid="email-optin-form">
            <h3 className="font-heading font-semibold text-lg text-[#0F172A]">Send results to your email (optional)</h3>
            <p className="text-sm text-slate-600 mt-1">We'll email you a summary so you can review it later. Your email is only stored if you submit it here.</p>
            <div className="mt-4 flex flex-col sm:flex-row gap-3">
                <Input type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className="min-h-[44px]" data-testid="input-email-optin" />
                <Button type="submit" disabled={sending} className="ccs-btn-secondary rounded-full whitespace-nowrap" data-testid="submit-email-optin">
                    {sending ? "Sending…" : "Email me the results"}
                </Button>
            </div>
            <label className="mt-3 flex items-start gap-2 text-xs text-slate-500">
                <Checkbox checked={consent} onCheckedChange={(v) => setConsent(Boolean(v))} data-testid="checkbox-consent" />
                <span>Occasionally email me tips about childcare & CCS updates. You can unsubscribe any time.</span>
            </label>
        </form>
    );
}

function DonationStrip() {
    const cfg = (typeof window !== "undefined" && window.__ccs_cfg) || null;
    // Lazy load via hook in footer; here use simple link
    return (
        <div className="ccs-card border-dashed border-[#22C55E]/50 bg-[#F0FDF4]" data-testid="donation-strip">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex-1">
                    <h3 className="font-heading font-semibold text-lg text-[#0F172A]">If this calculator helped you</h3>
                    <p className="text-sm text-slate-600 mt-1">You can support this free tool with a small tip. It helps keep the rates up to date.</p>
                </div>
                <a
                    href={(cfg && cfg.donation_url) || "https://buymeacoffee.com/placeholder"}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => trackEvent("donation_click")}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#22C55E] text-white font-medium hover:bg-[#16A34A] transition-colors whitespace-nowrap"
                    data-testid="results-donate-button"
                >
                    Support this free calculator <ExternalLink className="h-4 w-4" />
                </a>
            </div>
        </div>
    );
}

function buildSummaryHtml(mode, data) {
    const r = data.result;
    const brand = "#1E3A8A";
    const green = "#22C55E";
    let body = "";

    if (mode === "estimator") {
        const t = r.target_child;
        body = `
            <tr><td><strong>CCS for this child:</strong> ${t.ccs_percent}% (${t.rate_type})</td></tr>
            <tr><td><strong>Standard CCS %:</strong> ${r.standard_ccs_percent}%</td></tr>
            ${r.higher_ccs_eligible ? `<tr><td><strong>Higher CCS available:</strong> ${r.higher_ccs_percent}%</td></tr>` : ""}
            <tr><td><strong>Subsidised hours / fortnight:</strong> ${r.subsidised_hours_per_fortnight}</td></tr>
            <tr><td><strong>Why this rate:</strong> ${r.uplift_reason}</td></tr>
        `;
    } else {
        const b = r.breakdown;
        body = `
            <tr><td><strong>Gap per day:</strong> $${r.totals.daily_parent}</td></tr>
            <tr><td><strong>Gap per fortnight:</strong> $${r.totals.fortnight_parent}</td></tr>
            <tr><td><strong>Approx monthly estimate:</strong> $${r.totals.monthly_parent}</td></tr>
            <tr><td><strong>Step-by-step:</strong><br/>
                A. ${b.A_full_fee.formula}<br/>
                B. ${b.B_charged_hours.formula}<br/>
                C. Used ${b.C_subsidised_hours.used} of ${b.C_subsidised_hours.approved} subsidised hrs<br/>
                D. ${b.D_cap.note}<br/>
                E. ${b.E_subsidy.formula}<br/>
                F. ${b.F_withholding.formula}<br/>
                G. ${b.G_net_ccs.formula}<br/>
                H. ${b.H_gap.fortnight_formula}
            </td></tr>
        `;
    }

    return `
    <div style="font-family:Arial,sans-serif;color:#0F172A;max-width:600px;margin:0 auto;padding:24px;">
      <h1 style="color:${brand};font-size:20px;margin:0 0 4px;">CCS Cost Calculator Australia</h1>
      <p style="color:#475569;margin:0 0 16px;">Your ${mode === "estimator" ? "CCS estimate" : "out-of-pocket cost"} summary (latest CCS rules)</p>
      <table cellpadding="8" cellspacing="0" border="0" style="width:100%;background:#F1F5F9;border:1px solid #E2E8F0;border-radius:8px;font-size:14px;">
        ${body}
      </table>
      <p style="color:${green};font-size:13px;margin-top:16px;">Latest CCS rules applied.</p>
      <p style="color:#475569;font-size:12px;">This is an estimate only. Actual CCS amounts are determined by Services Australia.</p>
    </div>
    `;
}
